/**
 * Groups UI view keys into the sections shown in the permission pickers.
 *
 * <h3>Why this is shared</h3>
 * UiViewProfilesPage and TeamMemberWizard each had their own copy of these four arrays.
 * They had already drifted apart AND both had gone stale: FLEET_CUSTOMERS
 * and every FLEET_* key were missing, so they could not be ticked in either screen —
 * silently, because an unlisted key was simply filtered away.
 *
 * <h3>Fail OPEN, not closed</h3>
 * ⚠️ The old `filter(k => names.includes(k.key))` dropped anything unrecognised. That is
 * the wrong direction for a picker: a key added to the backend enum vanished from the UI
 * with no error, no warning, and no way to grant it — which is exactly how the existing
 * keys went missing.
 *
 * Unknown keys now fall into a trailing "Other" section instead. A new backend key shows
 * up immediately, in the wrong group at worst, rather than disappearing.
 */

/** Section order and membership. A key may appear in exactly one. */
const SECTIONS = [
    {
        label: 'Operations',
        keys: [
            'BOOKINGS', 'QUOTES', 'DASHBOARD', 'CUSTOMER_COMMUNICATIONS', 'INVOICES', 'JOBS',
            'VEHICLES', 'SERVICE_RECORDS', 'FLEET_CUSTOMERS', 'CUSTOMER_REVIEWS',
        ],
    },
    {
        label: 'Resources & Inventory',
        keys: ['RESOURCE_PLANNING', 'STOCK_MANAGEMENT', 'PROCUREMENT'],
    },
    {
        label: 'Configuration',
        keys: [
            'WORK_ITEMS', 'SERVICE_PROFILES', 'TEAM_MEMBERS', 'STAFF_PERMISSION_PROFILES',
            'UI_VIEW_PROFILES', 'ACCOUNTING_SOFTWARE', 'ORGANISATION',
        ],
    },
    {
        label: 'Fleet Portal',
        keys: [
            'FLEET_VEHICLES', 'FLEET_BOOKINGS', 'FLEET_INVOICES',
            'FLEET_SERVICE_RECORDS',
        ],
    },
    {
        label: 'Profile Menu',
        keys: ['MY_PROFILE', 'SECURITY', 'SETTINGS', 'HELP_SUPPORT'],
    },
];

/**
 * Bucket the available keys into display sections.
 *
 * @param {Array<{key: string, label?: string}>} availableKeys as returned by the backend
 * @returns {Array<{label: string, keys: Array}>} empty sections removed, "Other" last
 */
export const groupUiViewKeys = (availableKeys) => {
    const keys = availableKeys || [];

    const grouped = SECTIONS.map(({ label, keys: names }) => ({
        label,
        keys: keys.filter((k) => names.includes(k.key)),
    }));

    // Anything the lists above do not know about. This is the fail-open half — without
    // it a new backend key is invisible and ungrantable.
    const known = new Set(SECTIONS.flatMap((s) => s.keys));
    const other = keys.filter((k) => !known.has(k.key));
    if (other.length > 0) {
        grouped.push({ label: 'Other', keys: other });
    }

    return grouped.filter((s) => s.keys.length > 0);
};

export default groupUiViewKeys;
